// @flow

import * as React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import {groupBy} from 'lodash';

import Box from 'sail/Box';
import Group from 'sail/Group';
import BrandIcon from 'sail/BrandIcon';
import Grid, {GridItem} from 'sail/Grid';
import Checkbox from 'sail/Checkbox';
import {TooltipContainer} from 'sail/Tooltip';
import {ButtonLink} from 'sail/Button';
import {Subheading} from 'sail/Text';

import {useAccount} from 'docs/data';
import {useGuide} from '../../data/GuideProvider';
import {useData} from '../../data/DataProvider';

const messages = defineMessages({
  mostPopular: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.mostPopular',
    defaultMessage: 'Most popular',
  },
  showAll: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.showAll',
    defaultMessage: 'Show all',
  },
  showLess: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.showLess',
    defaultMessage: 'Show less',
  },
  disabledTooltip: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.disabledTooltip',
    defaultMessage:
      'This payment method does not support {currency}. Try using a price in one of the following currencies: {supportedCurrencies}.',
  },
});

const paymentMethodLabels = defineMessages({
  card: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.card',
    defaultMessage: 'Cards',
  },
  ideal: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.ideal',
    defaultMessage: 'iDEAL',
  },
  p24: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.p24',
    defaultMessage: 'P24',
  },
  bancontact: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.bancontact',
    defaultMessage: 'Bancontact',
  },
  sepa_debit: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.sepaDebit',
    defaultMessage: 'SEPA Direct Debit',
  },
  sofort: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.sofort',
    defaultMessage: 'Sofort',
  },
  giropay: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.giropay',
    defaultMessage: 'giropay',
  },
  eps: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.eps',
    defaultMessage: 'EPS',
  },
  fpx: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.fpx',
    defaultMessage: 'FPX',
  },
  alipay: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.alipay',
    defaultMessage: 'Alipay',
  },
  afterpay_clearpay: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.afterpayClearpay',
    defaultMessage: 'Afterpay / Clearpay',
  },
  grabpay: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.grabpay',
    defaultMessage: 'GrabPay',
  },
  bacs_debit: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.bacsDebit',
    defaultMessage: 'Bacs Direct Debit',
  },
  wechat_pay: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.wechatPay',
    defaultMessage: 'WeChat Pay',
  },
  boleto: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.boleto',
    defaultMessage: 'Boleto',
  },
  acss_debit: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.acssDebit',
    defaultMessage: 'Canadian PADs',
  },
  oxxo: {
    id: 'docsApp.integrationBuilder.demo.paymentMethodPicker.oxxo',
    defaultMessage: 'OXXO',
  },
});

const paymentMethodIconOverrides = {
  bacs_debit: 'directdebit',
  sepa_debit: 'sepa',
  afterpay_clearpay: 'afterpayclearpay',
  acss_debit: 'bank',
  wechat_pay: 'wechat',
};

const enablePaymentMethods = {
  AT: ['eps', 'sofort'],
  BE: ['bancontact', 'sofort'],
  BR: ['boleto'],
  CA: ['acss_debit'],
  DE: ['giropay', 'sofort'],
  ES: ['sofort'],
  GB: ['bacs_debit'],
  IT: ['sofort'],
  MX: ['oxxo'],
  MY: ['fpx', 'grabpay'],
  NL: ['ideal', 'sofort'],
  PL: ['p24'],
  SG: ['grabpay'],
};

// TODO(mfix) migrate this to use PAYMENT_METHODS in docs/lib/contexts/contrib/checkout_payment_methods/checkout_payment_methods.rb when this is in Markdoc
const currencyRestrictions = {
  ideal: ['eur'],
  p24: ['eur', 'pln'],
  bancontact: ['eur'],
  sepa_debit: ['eur'],
  sofort: ['eur'],
  giropay: ['eur'],
  eps: ['eur'],
  fpx: ['myr'],
  alipay: [
    'aud',
    'cad',
    'eur',
    'gbp',
    'hkd',
    'jpy',
    'nzd',
    'sgd',
    'usd',
    'myr',
    'cny',
  ],
  afterpay_clearpay: ['usd'],
  grabpay: ['sgd', 'myr'],
  bacs_debit: ['gbp'],
  wechat_pay: [
    'aud',
    'cny',
    'cad',
    'chf',
    'eur',
    'dkk',
    'nok',
    'sek',
    'gbp',
    'hkd',
    'jpy',
    'sgd',
    'usd',
  ],
  boleto: ['brl'],
  acss_debit: ['cad', 'usd'],
  oxxo: ['mxn'],
};

export function PaymentMethodPicker() {
  const {activeDemo, updateDemo} = useGuide();
  const {activePrice} = useData();
  const [showAll, setShowAll] = React.useState(false);
  const {
    account: {
      merchant: {country},
    },
  } = useAccount();
  const initialSetPaymentMethod = React.useRef(setPaymentMethod);
  const activeCurrency = activePrice?.currency;

  const currentPaymentMethods = new Set(
    activeDemo?.settings?.paymentMethods || [],
  );

  function setPaymentMethod(key, value) {
    if (!activeDemo) {
      return;
    }

    if (value) {
      currentPaymentMethods.add(key);
    } else {
      currentPaymentMethods.delete(key);
    }

    return updateDemo({
      ...activeDemo,
      settings: {
        ...activeDemo.settings,
        paymentMethods: [...currentPaymentMethods],
      },
    });
  }

  React.useEffect(() => {
    if (enablePaymentMethods[country]) {
      enablePaymentMethods[country].forEach((key) => {
        initialSetPaymentMethod.current(key, true);
      });
    }
  }, [country]);

  let methods = Object.keys(paymentMethodLabels);

  const {available = [], unavailable = []} = groupBy(methods, (type) => {
    const currencyRestriction = currencyRestrictions[type];
    if (
      activeCurrency &&
      currencyRestriction &&
      !currencyRestriction.includes(activeCurrency)
    ) {
      return 'unavailable';
    }
    return 'available';
  });

  if (enablePaymentMethods[country]) {
    enablePaymentMethods[country].forEach((key, i, array) => {
      // Move popular payment methods in current country to the beginning of the array, after 'card'
      const index = available.indexOf(key);
      available.splice(i + 1, 0, available.splice(index, 1)[0]);
    });
  }

  // Show available payment methods first
  methods = [...available, ...unavailable];

  if (!showAll) {
    methods = methods.slice(0, 6);
  }

  return (
    <Box padding={{top: 20, bottom: 8}}>
      <Group
        direction="horizontal"
        justifyContent="space-between"
        padding={{bottom: 12}}
      >
        <Subheading color="default">
          <FormattedMessage {...messages.mostPopular} />
        </Subheading>
        <ButtonLink
          label={
            <FormattedMessage
              {...(showAll ? messages.showLess : messages.showAll)}
            />
          }
          onClick={() => setShowAll((s) => !s)}
          icon={showAll ? 'chevronUp' : 'chevronDown'}
          iconPosition="right"
        />
      </Group>
      <Grid gap={8}>
        {methods.map((type, i) => {
          const checked = currentPaymentMethods.has(type);
          const disabled = unavailable.includes(type);
          const currencyRestriction = currencyRestrictions[type];

          return (
            <GridItem
              key={type}
              columnSpan={6}
              column={i % 2 === 1 ? 7 : 1}
              row={Math.floor(i / 2) + 1}
            >
              <TooltipContainer
                position={{context: 'topleft', layer: 'bottomleft'}}
                offsetX={6}
                offsetY={4}
                context={
                  <Checkbox
                    checked={checked && !disabled}
                    disabled={disabled}
                    onChange={() => setPaymentMethod(type, !checked)}
                    label={
                      <Group
                        direction="horizontal"
                        spacing={8}
                        alignItems="center"
                        style={{position: 'relative', top: '+3px'}}
                      >
                        <BrandIcon
                          icon={paymentMethodIconOverrides[type] || type}
                          size={16}
                        />
                        <FormattedMessage {...paymentMethodLabels[type]} />
                      </Group>
                    }
                  />
                }
                tooltipContents={
                  disabled ? (
                    <FormattedMessage
                      {...messages.disabledTooltip}
                      values={{
                        currency: activeCurrency?.toUpperCase(),
                        supportedCurrencies: currencyRestriction
                          .map((s) => s.toUpperCase())
                          .sort((a: string, b: string) => a.localeCompare(b))
                          .join(', '),
                      }}
                    />
                  ) : null
                }
              />
            </GridItem>
          );
        })}
      </Grid>
    </Box>
  );
}
